import { COUNTRIES, Countries } from '@constants/countries';
import { PLACEHOLDER_IMAGE } from '@constants/global';
import {
  COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME,
  COMPANY_BANKING_DETAILS_BANK_CODE,
  COMPANY_BANKING_DETAILS_BANK_NUMBER,
  COMPANY_BANKING_DETAILS_COUNTRY,
  COMPANY_BILLING_INFO_CITY,
  COMPANY_BILLING_INFO_CODICE_FISCALE,
  COMPANY_BILLING_INFO_COUNTRY,
  COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON,
  COMPANY_BILLING_INFO_LEGAL_NAME,
  COMPANY_BILLING_INFO_POSTCODE,
  COMPANY_BILLING_INFO_REGISTERED_ADDRESS,
  COMPANY_BILLING_INFO_VAT_NUMBER,
  COMPANY_FINANCE_NOTIFICATIONS_EMAILS,
  COMPANY_FINANCE_NOTIFICATIONS_PHONE,
  COMPANY_SETTING_BANKING_DETAILS,
  COMPANY_SETTING_BILLING_INFO,
  COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS,
  COMPANY_SETTING_FINANCE_NOTIFICATIONS,
  COMPANY_SETTING_LOGO_URL,
  COMPANY_SETTING_PUBLIC_NAME,
  COMPANY_SETTINGS_COUNTRY,
  COMPANY_SETTINGS_COUNTRY_ISO_CODE,
  COMPANY_SETTINGS_IMAGE_INPUT_NAME,
  CompanySettings,
  CompanySettingsFormTypes,
} from '@domain/company-settings';
import { CompanyCountry } from '@hooks/company/use-get-countries';
import { clearEmptyValues } from '@utils/clear-empty-fields';

import { CreateCompanyFieldProps, CreateFieldProps, FieldConfig } from '../company-settings-form.type';

export const SEPA_COUNTRIES = [
  'IT', // Italy
  'ES', // Spain
  'FR', // France
  'PT', // Portugal
  'DK', // Denmark
  'LU', // Luxembourg
  'BE', // Belgium
  'DE', // Germany
  'NL', // Netherlands
  'IE', // Ireland
  'SI', // Slovenia
  'GR', // Greece
  'BG', // Bulgaria
  'MC', // Monaco
  'AT', // Austria
  'FI', // Finland
  'CY', // Cyprus
  'EE', // Estonia
  'LV', // Latvia
  'LT', // Lithuania
  'MT', // Malta
  'PL', // Poland
  'CZ', // Czech Republic
  'SK', // Slovakia
  'HU', // Hungary
  'SE', // Sweden
  'HR', // Croatia
  'RO', // Romania
  'CH', // Switzerland
  'AD', // Andorra
];

export const createField = ({
  id,
  t,
  extraProps,
  dataType,
  isRequired = false,
  labelKey,
  options,
  disabled = false,
  readOnly = false,
}: CreateFieldProps): FieldConfig => ({
  id,
  name: labelKey ? t(labelKey) : '',
  slug: id,
  dataType,
  isRequired,
  disabled,
  readOnly,
  ...(options && { options }),
  ...(extraProps && { extraProps }),
});

export const createCompanyField = ({
  id,
  t,
  extraProps,
  dataType,
  labelKey,
  isRequired = false,
  disabled = false,
  readOnly = false,
}: CreateCompanyFieldProps) => createField({ id, t, dataType, isRequired, labelKey, disabled, readOnly, extraProps });

export const isCountrySEPA = (country?: string) => (country ? SEPA_COUNTRIES.includes(country) : false);

export const formatCompanyCountries = (companyCountries: CompanyCountry[]): Countries => {
  const orderedCountries = new Map(
    companyCountries.map((country) => [
      country.isoCode,
      {
        id: country.id,
        label: country.name,
        isoCode: country.isoCode,
        dialCode: COUNTRIES[country.isoCode]?.dialCode || '',
      },
    ])
  );

  return Object.fromEntries(orderedCountries);
};

export const getCountryId = ({ country, countries }: { country: string; countries?: Countries }) => {
  return countries?.[country].id ?? '';
};

export const getCardDisclosureDescription = ({
  billingInfo,
  bankingDetails,
  warningBillingInfo,
  warningBankingFields,
}: {
  billingInfo: Record<string, string>;
  bankingDetails: Record<string, string>;
  warningBillingInfo: string;
  warningBankingFields: string;
}) => {
  const hasVatNumber = billingInfo[COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON] === 'false';
  const isItalianCompany = billingInfo.country === 'IT';

  const hasEmptyBillingFields = Object.keys(billingInfo).some((key) => {
    const value = billingInfo[key];
    if (key === COMPANY_BILLING_INFO_VAT_NUMBER) return hasVatNumber && !value;
    if (key === COMPANY_BILLING_INFO_CODICE_FISCALE) return hasVatNumber && isItalianCompany && !value;

    return !value;
  });
  const hasEmptyBankingFields = Object.values(bankingDetails).some((value) => !value);

  if (!hasEmptyBillingFields && !hasEmptyBankingFields) {
    return '';
  }

  if (hasEmptyBillingFields && hasEmptyBankingFields) {
    return `${warningBillingInfo}\n${warningBankingFields}`;
  }

  if (hasEmptyBillingFields) {
    return warningBillingInfo;
  }

  return warningBankingFields;
};

// The backend stores every multi-email field as an array; the textarea holds a
// comma-separated string. `null` is the cleared value the PATCH expects.
// filter(Boolean) drops the empty segment a trailing or doubled comma leaves behind — the BE
// 400s on any invalid address. multiEmail validation already rejects those before Save enables,
// but this helper is shared with financeNotifications, so it does not rely on the caller.
const toEmailList = (value: string) =>
  value.trim() === '' ? null : value.replace(/\s+/g, '').split(',').filter(Boolean);

// The form validates some fields programmatically on mount (the trigger() calls in
// company-settings-form), so `errors` can be populated for data the user never touched —
// gating Save on it raw would lock out a company whose stored IBAN no longer validates.
// trigger() does not mark a field touched, but the field onChange does, so only errors on
// fields the user actually edited count.
export const hasTouchedFieldError = (errors: object, touchedFields: object): boolean =>
  Object.entries(touchedFields).some(([key, isTouched]) => {
    const error = (errors as Record<string, unknown>)[key];
    if (!error) return false;
    if (isTouched === true) return true;

    return typeof isTouched === 'object' && hasTouchedFieldError(error as object, isTouched as object);
  });

export const getDefaultValues = ({ companyInfo }: { companyInfo?: CompanySettings }) => {
  return {
    [COMPANY_SETTING_PUBLIC_NAME]: companyInfo?.[COMPANY_SETTING_PUBLIC_NAME] ?? '',
    [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: companyInfo?.[COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]?.join(',') ?? '',
    [COMPANY_BILLING_INFO_COUNTRY]:
      companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_SETTINGS_COUNTRY]?.[COMPANY_SETTINGS_COUNTRY_ISO_CODE] ?? '',
    [COMPANY_BANKING_DETAILS_COUNTRY]:
      companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_SETTINGS_COUNTRY]?.[COMPANY_SETTINGS_COUNTRY_ISO_CODE] ??
      '',
    [COMPANY_SETTING_LOGO_URL]: companyInfo?.[COMPANY_SETTING_LOGO_URL] ?? PLACEHOLDER_IMAGE,
    [COMPANY_SETTING_BILLING_INFO]: {
      [COMPANY_BILLING_INFO_LEGAL_NAME]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_LEGAL_NAME] ?? '',
      [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_REGISTERED_ADDRESS] ?? '',
      [COMPANY_BILLING_INFO_POSTCODE]: companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_POSTCODE] ?? '',
      [COMPANY_BILLING_INFO_CITY]: companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_CITY] ?? '',
      [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]?.toString() ?? 'false',
      [COMPANY_BILLING_INFO_VAT_NUMBER]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_VAT_NUMBER] ?? '',
      [COMPANY_BILLING_INFO_CODICE_FISCALE]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_CODICE_FISCALE] ?? '',
    },
    [COMPANY_SETTING_BANKING_DETAILS]: {
      [COMPANY_BANKING_DETAILS_BANK_NUMBER]:
        companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_BANKING_DETAILS_BANK_NUMBER] ?? '',
      [COMPANY_BANKING_DETAILS_BANK_CODE]:
        companyInfo?.[COMPANY_SETTING_BANKING_DETAILS][COMPANY_BANKING_DETAILS_BANK_CODE] ?? '',
      [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]:
        companyInfo?.[COMPANY_SETTING_BILLING_INFO][COMPANY_BILLING_INFO_LEGAL_NAME] ?? '',
    },
    [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
      [COMPANY_FINANCE_NOTIFICATIONS_PHONE]:
        companyInfo?.[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_PHONE] ?? '',
      [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]:
        companyInfo?.[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_EMAILS]?.join(',') ?? '',
    },
  };
};

export const getCompanySettingsFromForm = ({
  formDirty,
  companyCountries,
}: {
  formDirty: Partial<CompanySettingsFormTypes>;
  companyCountries?: Countries;
}) => {
  const {
    [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: _logoUrl,
    [COMPANY_BILLING_INFO_COUNTRY]: billingCountry,
    [COMPANY_BANKING_DETAILS_COUNTRY]: bankCountry,
    ...formSettings
  } = formDirty;

  // Remove the read-only accountHolderName field from banking details if it exists
  if (formSettings[COMPANY_SETTING_BANKING_DETAILS]?.[COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME] !== undefined) {
    const { [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]: accountHolderName, ...restBankingDetails } =
      formSettings[COMPANY_SETTING_BANKING_DETAILS];
    formSettings[COMPANY_SETTING_BANKING_DETAILS] = restBankingDetails;
  }

  const customerSupportEmails = formSettings[COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS];

  const transformedSettings = {
    ...formSettings,
    ...(customerSupportEmails !== undefined && {
      [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: toEmailList(customerSupportEmails),
    }),
    [COMPANY_SETTING_BILLING_INFO]: {
      ...formSettings[COMPANY_SETTING_BILLING_INFO],
      ...(billingCountry && {
        countryId: getCountryId({ country: billingCountry, countries: companyCountries }),
      }),
      ...(formSettings[COMPANY_SETTING_BILLING_INFO] &&
        COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON in formSettings[COMPANY_SETTING_BILLING_INFO] && {
          [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]:
            formSettings[COMPANY_SETTING_BILLING_INFO]?.[COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON] === 'true',
        }),
    },
    [COMPANY_SETTING_BANKING_DETAILS]: {
      ...formSettings[COMPANY_SETTING_BANKING_DETAILS],
      ...(bankCountry && {
        countryId: getCountryId({ country: bankCountry, countries: companyCountries }),
      }),
    },
    [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
      ...formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS],
      ...(formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS] &&
        COMPANY_FINANCE_NOTIFICATIONS_EMAILS in formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS] && {
          [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]: toEmailList(
            formSettings[COMPANY_SETTING_FINANCE_NOTIFICATIONS][COMPANY_FINANCE_NOTIFICATIONS_EMAILS]
          ),
        }),
    },
  };

  return clearEmptyValues(transformedSettings);
};
